/**
 * TanStack Query hooks for `campaign-dashboard`. Read-only surface — the
 * backend does not expose write endpoints, so we only provide list + stats
 * queries plus a manual invalidator the UI can call to refresh.
 */
import {
	useQuery,
	useQueryClient,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	listCampaigns,
	fetchStats,
	campaignDashboardQueryKeys,
	type ListCampaignsResponse,
	type ListFilters,
	type StatsResponse,
} from "./api";

/**
 * Paginated, filterable list of campaigns for the tenant in session.
 * Keeps previous data between filter tweaks so the table does not flash.
 */
export function useCampaignList(
	filters: ListFilters = {},
): UseQueryResult<ListCampaignsResponse, Error> {
	return useQuery({
		queryKey: campaignDashboardQueryKeys.list(filters),
		queryFn: () => listCampaigns(filters),
		staleTime: 15_000,
	});
}

/**
 * Header KPI rollup (totals + per-state counts). Lightweight — safe to leave
 * on its own short stale window.
 */
export function useCampaignStats(): UseQueryResult<StatsResponse, Error> {
	return useQuery({
		queryKey: campaignDashboardQueryKeys.stats(),
		queryFn: fetchStats,
		staleTime: 15_000,
	});
}

/**
 * Returns a callback that invalidates every campaign-dashboard query. Used
 * by the "Refresh" button and by any future mutations that need to bust the
 * cache after the backend gains write endpoints.
 */
export function useInvalidateCampaignDashboard(): () => void {
	const qc = useQueryClient();
	return () => {
		qc.invalidateQueries({ queryKey: campaignDashboardQueryKeys.all });
	};
}
